import {
  getCarsSearchColorsMasterManufacturerCodeMasterModelCode,
  getCarsSearchLeasingManufacturerManufacturerCodeModelModelCode,
  getCarsSearchSaleVehicleNumber,
  postCarsSearchSearchLeasing,
  SearchCarsRequest,
} from "api"

export type Params = { id: string; locale: "he" | "en" }
export type LeasingParams = {
  manufacturerCode: string
  modelCode: string
  locale: "he" | "en"
}

export interface TrimOption {
  vehicleNumber: number
  trimLevel: string
  trimLevelDisplay: string
  price: number
  oldPrice?: number
  listPrice?: number
  // Color codes that exist in inventory for this trim — the configurator
  // only offers colors a real vehicle of the selected trim has
  availableColorCodes: number[]
}

export interface EngineGroup {
  engineCapacity: number
  engineLabel: string
  trims: TrimOption[]
}

export interface ConfigurationData {
  carData: any
  engineOptions: any[]
  engineGroups: EngineGroup[]
  colors: {
    colorCode?: number
    color: string
    label: string
    price: number
    spinCarId?: string
  }[]
}

export interface FinishLevel {
  name: string
  description: string
  monthlyPrice: number
  trimLevel: string
}

export interface PackageOption {
  name: string
  description: string
  monthlyPrice: number
}

export interface LeasingConfigurationData extends ConfigurationData {
  finishLevels: FinishLevel[]
  additionalPackages: PackageOption[]
}

export async function fetchCarConfigurationData({
  id,
  locale,
}: {
  id: string
  locale: "he" | "en"
}): Promise<ConfigurationData> {
  // Get the car data first
  const res = await getCarsSearchSaleVehicleNumber(id, locale)
  const carData = res.data.body

  // Fetch color options for this car model
  const colorsRes =
    await getCarsSearchColorsMasterManufacturerCodeMasterModelCode(
      carData.masterManufacturerCode,
      carData.masterModelCode,
    )

  // Use variants from the API (server-side grouping by manufacturerCode + modelCode)
  // variants[0] is the lowest-priced car; for non-duplicates, variants has a single entry
  const inventoryCars = carData.variants?.length > 0 ? carData.variants : [carData]

  // The configurator presents configurations for new cars only, but the server-side
  // variant grouping (master model family) can include used cars of the same model —
  // exclude them so their prices/colors don't appear as engine or color options
  const newCars = inventoryCars.filter(
    (car) => car.carClassification !== "UsedCar",
  )
  const allCars = newCars.length > 0 ? newCars : [carData]

  // Build colors from inventory, enriched with pricing from color API
  const colorApiMap = new Map<
    number,
    { hex: string; desc: string; price: number }
  >()
  for (const c of colorsRes.data.body?.colors || []) {
    colorApiMap.set(c.colorCode, {
      hex: c.colorHexadecimalCode,
      desc: c.colorDescription,
      price: c.colorPrice,
    })
  }

  const seenColorCodes = new Set<number>()
  const colors: {
    colorCode?: number
    color: string
    label: string
    price: number
    spinCarId?: string
  }[] = []
  for (const car of allCars) {
    if (car.colorCode && !seenColorCodes.has(car.colorCode)) {
      seenColorCodes.add(car.colorCode)
      const apiColor = colorApiMap.get(car.colorCode)
      const hex = apiColor?.hex || car.colorHexadecimalCode || ""
      if (!hex) continue
      colors.push({
        colorCode: car.colorCode,
        color: hex,
        label: apiColor?.desc || car.colorDescription || "",
        price: apiColor?.price || 0,
        spinCarId: car.detailedImages?.spinCarId || "",
      })
    }
  }

  // Create a map to group cars by engineCapacity, deduplicating by trimLevel.
  // Cars sharing a trim still contribute their color to that trim's
  // availableColorCodes, so the color step only offers colors in inventory
  const engineGroupMap = new Map<number, TrimOption[]>()

  for (const car of allCars) {
    const capacity = car.engineCapacity
    const trimKey = car.trimLevel || ""

    if (!engineGroupMap.has(capacity)) {
      engineGroupMap.set(capacity, [])
    }
    const trims = engineGroupMap.get(capacity)!
    const existingTrim = trims.find((trim) => trim.trimLevel === trimKey)
    if (existingTrim) {
      if (
        car.colorCode &&
        !existingTrim.availableColorCodes.includes(car.colorCode)
      ) {
        existingTrim.availableColorCodes.push(car.colorCode)
      }
      continue
    }

    trims.push({
      vehicleNumber: car.vehicleNumber,
      trimLevel: trimKey,
      trimLevelDisplay: car.trimLevel || car.carZone?.name?.trimName || "",
      price: Number(car.advertisedPrice),
      oldPrice: car.discountedPrice ? Number(car.discountedPrice) : undefined,
      listPrice: Number(car.leviYitzhakPrice) || undefined,
      availableColorCodes: car.colorCode ? [car.colorCode] : [],
    })
  }

  // Convert map to array and sort trims by price within each engine group
  const engineGroups: EngineGroup[] = Array.from(engineGroupMap.entries())
    .map(([capacity, trims]) => ({
      engineCapacity: capacity,
      engineLabel: `מנוע ${capacity / 1000}L`,
      trims: trims.sort((a, b) => a.price - b.price), // Sort by price ascending
    }))
    .sort((a, b) => a.engineCapacity - b.engineCapacity) // Sort engines by capacity

  // Keep backward-compatible flat engineOptions (using cheapest trim per engine)
  const engineOptions = engineGroups.map((group) => ({
    price: group.trims[0].price,
    oldPrice: group.trims[0].oldPrice,
    engine: group.engineLabel,
    vehicleNumber: group.trims[0].vehicleNumber,
    listPrice: group.trims[0].listPrice,
  }))

  return {
    carData,
    engineOptions,
    engineGroups,
    colors,
  }
}

export async function fetchLeasingConfigurationData({
  manufacturerCode,
  modelCode,
  locale,
}: LeasingParams): Promise<LeasingConfigurationData> {
  // Get the leasing car data first

  const res =
    await getCarsSearchLeasingManufacturerManufacturerCodeModelModelCode(
      manufacturerCode,
      modelCode,
      locale,
    )
  const carData = res.data.body

  const colorsRes =
    await getCarsSearchColorsMasterManufacturerCodeMasterModelCode(
      carData.masterManufacturerCode,
      carData.masterModelCode,
      locale,
    )

  // Fetch all leasing cars from same manufacturer, then filter to same model family
  const similarCarsRes = await postCarsSearchSearchLeasing(
    {
      requestPage: 0,
      pageSize: 100,
      manufacturers: [carData.manufacturerCode],
    } as SearchCarsRequest,
    locale,
  )

  // Filter to same model using modelCode (not masterModelCode which groups different models)
  const similarCars = similarCarsRes.data.body.items.filter(
    (car) => car.modelCode === carData.modelCode,
  )

  // Build colors from inventory, enriched with pricing from color API
  const allLeasingCars = [
    carData,
    ...similarCars.filter((car) => car.id !== carData.id),
  ]

  const colorApiMap = new Map<
    string,
    { hex: string; desc: string; price: number }
  >()
  for (const c of colorsRes.data.body?.colors || []) {
    colorApiMap.set(String(c.colorCode), {
      hex: c.colorHexadecimalCode,
      desc: c.colorDescription,
      price: c.colorPrice,
    })
  }

  // LeasingCarDto stores colors in a nested `colors` array
  const seenColorCodes = new Set<string>()
  const colors: { color: string; label: string; price: number }[] = []
  for (const car of allLeasingCars) {
    if (car.colors) {
      for (const c of car.colors) {
        const code = String(c.colorCode)
        if (c.colorCode && !seenColorCodes.has(code)) {
          seenColorCodes.add(code)
          const apiColor = colorApiMap.get(code)
          colors.push({
            color: apiColor?.hex || c.colorHexadecimalCode || "",
            label: apiColor?.desc || c.colorDescription || "",
            price: apiColor?.price || 0,
          })
        }
      }
    }
  }

  // Always include the base car as the first option, then add cars with different engine capacity
  const baseCarOption = {
    price:
      carData.monthlyPaymentSalePriceIncludingVat ||
      carData.monthlyPaymentPriceIncludingVat,
    oldPrice:
      carData.monthlyPaymentSalePriceIncludingVat &&
      carData.monthlyPaymentPriceIncludingVat !==
        carData.monthlyPaymentSalePriceIncludingVat
        ? carData.monthlyPaymentPriceIncludingVat
        : undefined,
    engine: `מנוע ${carData.engineCapacity / 1000}L`,
    vehicleNumber: carData.id, // Using id instead of vehicleNumber for leasing
    listPrice: carData.newCarParallelImporterPriceList || undefined,
  }

  // Filter out cars that have the same engine capacity as the base car
  const differentEngineCars = similarCars.filter(
    (car) => car.engineCapacity !== carData.engineCapacity,
  )

  const additionalOptions = differentEngineCars.map((car) => ({
    price:
      car.monthlyPaymentSalePriceIncludingVat ||
      car.monthlyPaymentPriceIncludingVat,
    oldPrice:
      car.monthlyPaymentSalePriceIncludingVat &&
      car.monthlyPaymentPriceIncludingVat !==
        car.monthlyPaymentSalePriceIncludingVat
        ? car.monthlyPaymentPriceIncludingVat
        : undefined,
    engine: `מנוע ${car.engineCapacity / 1000}L`,
    vehicleNumber: car.id, // Using id instead of vehicleNumber for leasing
    listPrice: car.newCarParallelImporterPriceList || undefined,
  }))

  // Combine base car with additional different engine options
  const engineOptions = [baseCarOption, ...additionalOptions]

  // Build finish levels from base car and similar cars with same engine but different trimLevel
  const baseTrimLevel = carData.carZone?.name?.trimNameEn || ""
  const baseTrimDisplayName =
    locale === "he"
      ? carData.carZone?.name?.trimName
      : carData.carZone?.name?.trimNameEn

  const finishLevels: FinishLevel[] = []

  // Only include the base finish level if the API provides a real trim name
  if (baseTrimDisplayName) {
    finishLevels.push({
      name: baseTrimDisplayName,
      description: carData.basePackageDescription || "",
      monthlyPrice: 0,
      trimLevel: baseTrimLevel,
    })
  }

  // Add finish levels from similar cars with same engine capacity but different trim
  const seenTrimLevels = new Set<string>([baseTrimLevel])
  const sameEngineCars = similarCars.filter(
    (car) =>
      car.engineCapacity === carData.engineCapacity && car.id !== carData.id,
  )
  for (const car of sameEngineCars) {
    const carTrimLevel = car.carZone?.name?.trimNameEn || ""
    if (carTrimLevel && !seenTrimLevels.has(carTrimLevel)) {
      seenTrimLevels.add(carTrimLevel)
      const displayName =
        locale === "he"
          ? car.carZone?.name?.trimName
          : car.carZone?.name?.trimNameEn
      if (!displayName) continue // Skip finish levels without a real name
      const baseMonthly =
        car.monthlyPaymentSalePriceIncludingVat ||
        car.monthlyPaymentPriceIncludingVat
      const carBaseMonthly =
        carData.monthlyPaymentSalePriceIncludingVat ||
        carData.monthlyPaymentPriceIncludingVat
      finishLevels.push({
        name: displayName,
        description: car.basePackageDescription || "",
        monthlyPrice: baseMonthly - carBaseMonthly,
        trimLevel: carTrimLevel,
      })
    }
  }
  // Sort by monthly price ascending
  finishLevels.sort((a, b) => a.monthlyPrice - b.monthlyPrice)

  // Extract packages from API and sort by price
  const additionalPackages: PackageOption[] = []

  if (carData.packages?.length > 0) {
    carData.packages.forEach((pkg) => {
      additionalPackages.push({
        name: pkg.packageName,
        description: pkg.packageDescription,
        monthlyPrice: pkg.packagePrice || 0,
      })
    })
    // Sort by price ascending (0 → 2000 → 3000)
    additionalPackages.sort((a, b) => a.monthlyPrice - b.monthlyPrice)
  }

  return {
    carData,
    engineOptions,
    engineGroups: [], // Leasing doesn't use engine groups
    colors,
    finishLevels,
    additionalPackages,
  }
}
